import { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { Button } from "../design/Button";

// Shared "load more" affordance for paged lists. Renders a button that also
// acts as an infinite-scroll sentinel: an IntersectionObserver triggers the
// next page when the row scrolls near the viewport, and the button stays as an
// explicit/fallback control (and for environments without the observer). The
// duplicate-call guard lives in usePagedQuery (hasMore/loadingMore), so it is
// safe to fire onLoadMore from both the observer and a click.
export function LoadMore({
  hasMore,
  loading,
  paused = false,
  onLoadMore,
}: {
  hasMore: boolean;
  loading: boolean;
  /**
   * Disables the infinite-scroll observer (e.g. after a failed page request,
   * see usePagedQuery.loadMoreFailed). The button stays as a manual retry.
   */
  paused?: boolean;
  onLoadMore: () => void;
}) {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || loading || paused) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "300px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, paused, onLoadMore]);

  if (!hasMore) return null;
  // Exactly one affordance at a time: a quiet text while a page is in
  // flight, the button otherwise. Swapping the label inside the button made
  // it resize and visually jump while scrolling.
  return (
    <div ref={ref} className="mt-5 flex h-9 items-center justify-center">
      {loading ? (
        <span className="text-smycles-label tracking-[0.15em] text-ink-muted">
          {intl.formatMessage({ id: "common.loadingMore", defaultMessage: "Loading…" })}
        </span>
      ) : (
        <Button
          variant="secondary"
          onClick={onLoadMore}
          className="px-5 py-2 text-smycles-label"
        >
          {intl.formatMessage({ id: "common.loadMore", defaultMessage: "Load more" })}
        </Button>
      )}
    </div>
  );
}
